import { IShip, IShipUser } from "../../Types"

// What a ship row in the select list offers. Exactly one state per row, and exactly
// one action per state -- this replaces the previous model where `companion` mapped to
// two different click behaviours chosen by the (invisible, volatile) local-discovery
// list, and where `alreadyOpened` rendered a distinct style it could never be clicked
// in (#1279).
export enum ShipSelectState {
  // No one holds this ship and I may take it: select it as primary.
  openable = "openable",
  // THIS device holds the claim token: resume it, never re-pair with it.
  openHere = "openHere",
  // Another device holds it: connect as a companion, always via the connect dialog.
  openElsewhere = "openElsewhere",
  // Not actionable right now. Always carries a reason unless the list itself is
  // display-only, so "can't connect right now" never degrades into "nothing happens".
  unavailable = "unavailable",
}

// Why a row is unavailable. Rendered as visible copy: the field reports behind #1279
// ("no dialog any more") could not distinguish "there is nothing to tap" from "tapping
// silently failed", so an unavailable row must say which it is.
export enum ShipSelectUnavailable {
  noPermission = "noPermission",
  importInProgress = "importInProgress",
  // Another ship is already open on THIS device. A device runs one primary logbook at
  // a time and the server refuses a second claim, so the list has to say so before the
  // click instead of letting the user pick a ship and meet a server error.
  shipOpenHere = "shipOpenHere",
}

// Where the companion-connect dialog currently is. One dialog serves both routes, so
// the automatic attempt and the manual form are phases of a single flow rather than
// two different overlays chosen before the click (#1279).
export enum CompanionConnectPhase {
  // Automatic attempt in flight: resolve the advertised service, then pair with it.
  connecting = "connecting",
  // Manual address entry. Reached directly (no automatic route) or as the fallback
  // after the automatic attempt failed or ran past its deadline.
  manual = "manual",
  // The primary refused this companion for running an older build. Terminal, and
  // deliberately NOT the manual form: no address the user can type changes the
  // outcome, so offering the form would invite them to keep retrying something
  // that cannot work. The only remedy is updating the app, which the view says.
  outdated = "outdated",
  success = "success",
  cancelled = "cancelled",
}

// Why a companion pairing did not complete. One value per cause the app can actually
// distinguish, because every one of them needs different advice: "nothing is
// advertising this ship" and "the primary refused you" are the same event only to the
// code that used to render both as `connect_failed` (#1388).
//
// Carried instead of ready-made copy so the workflow stays free of any translation
// dependency and every branch can be asserted without rendering; `ConnectView` owns
// the mapping to localized text.
export enum CompanionConnectError {
  // The attempt ran past its deadline without answering either way.
  timeout = "timeout",
  // Nothing on the network is advertising this ship.
  notDiscovered = "notDiscovered",
  // Several devices advertise it, so the fail-closed collision policy refuses to guess.
  ambiguous = "ambiguous",
  // Advertised, but no address could be determined for it.
  unresolved = "unresolved",
  // An address was known and nothing answered on it.
  unreachable = "unreachable",
  // The primary answered and declined to issue a session.
  refused = "refused",
  // No primary currently holds this ship, or this device has not synced since one did.
  notClaimed = "notClaimed",
  // The signed-in user is not a member of this ship's account.
  notAccessible = "notAccessible",
  // A manually entered address was not `host:port`.
  addressInvalid = "addressInvalid",
  // This device is the ship's own primary, so there is nothing to pair with (#1279).
  ownDevice = "ownDevice",
  // Nothing more specific is known. Kept so no failure path can become silent.
  failed = "failed",
}

// Why a ship's advertised service could not be resolved to an address, as reported by
// `ships.resolveLocal`. Mirrors the app-local route's `LocalShipResolveReason`.
export enum CompanionResolveReason {
  notDiscovered = "notDiscovered",
  ambiguous = "ambiguous",
  unresolved = "unresolved",
}

export interface ShipSelectViewProps {
  // Ship ids currently advertised on the LAN, from the app's local ships route.
  nearbyShipIds?: number[];
  // The ship whose claim token belongs to THIS device (`IShipsResponse.ownShipId`).
  ownShipId?: number;
  ships: IShip[];
  users?: IShipUser[];
  viewable?: boolean;
}

export interface ShipSelectRowParams {
  ship: IShip;
  // This user's membership row for this ship, from the ships-list response.
  user?: IShipUser;
  // Undefined means "no ship is known to be mine" (always the case in a cloud
  // session); it must never be widened into a match.
  ownShipId?: number;
  // Presence is positive evidence only: duplicate advertisements for one account+ship
  // are deliberately omitted upstream (fail-closed collision policy), so absence means
  // "no unambiguous automatic route", NOT "not on the network".
  nearbyShipIds?: number[];
  primaryFeature?: boolean;
  // The shared-ships list renders names only.
  viewable?: boolean;
}

export interface ShipSelectRow {
  state: ShipSelectState;
  unavailable?: ShipSelectUnavailable;
  // Set when the row keeps its natural state -- and so its own icons and control --
  // but cannot be acted on right now, carrying the reason why. Distinct from
  // `unavailable`, which means the row has no action at all and renders as a label:
  // a ship blocked only because another is open is still a real, ordinary row, and
  // showing it as an inert label would lose the icons that say what it is (#1279).
  blocked?: ShipSelectUnavailable;
  nearby: boolean;
}

export interface ShipSelectRowViewProps {
  row: ShipSelectRow;
  ship: IShip;
  // Set while THIS row's primary select is in flight.
  selecting: boolean;
  // True while any row's select is in flight, which blocks starting a second one.
  selectBlocked: boolean;
  onOpen: (shipId: number) => void;
  onConnect: (shipId: number, nearby: boolean) => void;
}

// The observable state of a companion-connect attempt. Every transition publishes one
// of these, so no exit path can be silent.
export interface CompanionConnectState {
  phase: CompanionConnectPhase;
  // Prefilled into the manual form when the mDNS record resolved before the failure.
  address?: string;
  // Why the route did not complete. Always set alongside a `manual` phase that was
  // reached from a failure, never when `manual` is the starting point.
  error?: CompanionConnectError;
  // True while a manual submit is in flight, so the form can disable its button.
  submitting?: boolean;
}
